import styled, { css } from "styled-components";
import { borderRadius, borderWidth, fontWeights, mediaQueries, spacing, typographyMixin } from "@magicbi/ui-library";
import { Chip as ChipBox } from "../../atoms/ChartStudioFieldChip/ChartStudioFieldChip.styles";
import type { TimelineTone } from "./Timeline.types";

/** The time column's width; the marker's centre and the joining line are measured from it. */
const TIME_COLUMN = "7.25rem";
const MARKER = spacing[8];
const GAP = spacing[3];

export const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Entry = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: ${TIME_COLUMN} ${MARKER} minmax(0, 1fr);
  column-gap: ${GAP};
  padding-bottom: ${spacing[5]};
  &:last-child {
    padding-bottom: 0;
  }
  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: calc(${TIME_COLUMN} + ${GAP} + (${MARKER} / 2) - 1px);
    top: calc(${MARKER} + ${spacing[1]});
    bottom: ${spacing[1]};
    width: ${borderWidth[2]};
    border-radius: ${borderRadius.xs};
    background: ${({ theme }) => theme.colors.border.subtle};
  }
  ${mediaQueries.down.sm} {
    grid-template-columns: ${MARKER} minmax(0, 1fr);
    &:not(:last-child)::before {
      left: calc((${MARKER} / 2) - 1px);
    }
  }
`;

const TONE_TEXT: Record<TimelineTone, ReturnType<typeof css> | null> = {
  default: null,
  start: null,
  end: null,
  error: css`
    color: ${({ theme }) => theme.colors.error.text};
  `,
  off: css`
    color: ${({ theme }) => theme.colors.text.secondary};
  `,
};

export const When = styled.div<{ $tone: TimelineTone }>`
  min-width: 0;
  padding-top: ${spacing[1]};
  text-align: right;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
  & > b {
    display: block;
    ${typographyMixin("body", 3)}
    font-weight: ${fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    ${({ $tone }) => TONE_TEXT[$tone]}
  }
  & > span {
    display: block;
  }
  ${mediaQueries.down.sm} {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: ${spacing[1]};
    text-align: left;
    & > b,
    & > span {
      display: inline;
    }
    & > *:not(:first-child)::before {
      content: "·";
      margin-right: ${spacing[1]};
    }
  }
`;

const TONE_MARKER: Record<TimelineTone, ReturnType<typeof css>> = {
  default: css`
    background: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.text.secondary};
    box-shadow: inset 0 0 0 ${borderWidth[2]} ${({ theme }) => theme.colors.border.default};
  `,
  start: css`
    background: ${({ theme }) => theme.colors.interactive.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
  `,
  end: css`
    background: ${({ theme }) => theme.colors.success[50]};
    color: ${({ theme }) => theme.colors.success.text};
    box-shadow: inset 0 0 0 ${borderWidth[2]} ${({ theme }) => theme.colors.success[500]};
  `,
  error: css`
    background: ${({ theme }) => theme.colors.error[50]};
    color: ${({ theme }) => theme.colors.error.text};
    box-shadow: inset 0 0 0 ${borderWidth[2]} ${({ theme }) => theme.colors.error[500]};
  `,
  off: css`
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.secondary};
    box-shadow: inset 0 0 0 ${borderWidth[2]} ${({ theme }) => theme.colors.border.subtle};
  `,
};

export const Marker = styled.span<{ $tone: TimelineTone }>`
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: ${MARKER};
  height: ${MARKER};
  border-radius: ${borderRadius.full};
  ${({ $tone }) => TONE_MARKER[$tone]}
  & > svg {
    width: 1rem;
    height: 1rem;
  }
  ${mediaQueries.down.sm} {
    grid-column: 1;
    grid-row: 1 / span 2;
  }
`;

export const Content = styled.div`
  min-width: 0;
  padding-top: ${spacing[1]};
  ${mediaQueries.down.sm} {
    grid-column: 2;
    grid-row: 2;
    padding-top: 0;
  }
`;

export const Title = styled.span<{ $tone: TimelineTone }>`
  display: block;
  ${typographyMixin("body", 3)}
  font-weight: ${fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  ${({ $tone }) => TONE_TEXT[$tone]}
`;

export const Body = styled.div`
  margin-top: ${spacing[0.5]};
  ${typographyMixin("body", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Detail = styled.span`
  display: block;
  margin-top: ${spacing[1]};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]};
  margin-top: ${spacing[2]};
`;

/** The field chip's box, in the theme's colours so it follows dark mode. */
export const Chip = styled(ChipBox)`
  border-color: ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** A chip's or note's icon, hidden from screen readers (the text says it all). */
export const SmallIcon = styled.span`
  display: inline-flex;
  flex: none;
  & > svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

export const Note = styled.span<{ $tone: "warning" | "neutral" }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[1]};
  margin-top: ${spacing[2]};
  ${typographyMixin("caption", 2)}
  color: ${({ theme, $tone }) => ($tone === "warning" ? theme.colors.warning.text : theme.colors.text.secondary)};
  & > ${SmallIcon} {
    margin-top: ${spacing[0.5]};
  }
`;

/** A text button inside an entry, as the step bodies draw them. */
export const ActionButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.link};
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
    border-radius: ${borderRadius.xs};
  }
`;
